import { Link } from "react-router-dom";
import {
  BookOpen, VideoCamera, Article, Certificate, ChatCenteredDots, Compass, ArrowRight,
} from "@phosphor-icons/react";
import { Section, PageHero, SectionHeading, FeatureCard, CalloutCard, EyebrowLabel } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const KITS = [
  {
    icon: BookOpen,
    title: "Documentation",
    description: "Full reference for the estimation, pit and reporting modules — with code samples and tuning cheatsheets.",
    to: "/docs",
  },
  {
    icon: VideoCamera,
    title: "Video Tutorials",
    description: "Step-by-step walkthroughs on domaining, kriging, pit optimisation and CPR assembly.",
    to: "/contact",
  },
  {
    icon: Article,
    title: "Blog & Insights",
    description: "Practical write-ups from the SRES geoscience team on modern resource work.",
    to: "/blog",
  },
  {
    icon: Certificate,
    title: "Case Studies",
    description: "Anonymised end-to-end deployments across Fe, Au, Cu, Ni and lithium projects.",
    to: "/case-studies",
  },
  {
    icon: ChatCenteredDots,
    title: "Webinars",
    description: "Live sessions on JORC, NI 43-101 and SAMREC reporting best practice.",
    to: "/contact",
  },
  {
    icon: Compass,
    title: "Onboarding Toolkit",
    description: "Everything a new SRES tenant needs — sample data, checklists and CPR templates.",
    to: "/support",
  },
];

const GUIDES = [
  { title: "The reproducible resource statement", tag: "Guide · 12 min", body: "How SRES snapshots every block model, pit run and CPR export so any past run can be re-downloaded byte-for-byte.", to: "/blog#the-reproducible-resource-statement" },
  { title: "Ordinary Kriging without the pain", tag: "Tutorial · 18 min", body: "A hands-on walkthrough of variogram fitting, composite lengths and per-domain estimation.", to: "/blog#ordinary-kriging-without-the-pain" },
  { title: "Monte-Carlo NPV done right", tag: "Playbook · 9 min", body: "Choosing triangular distributions, interpreting P10/P50/P90 and communicating risk to boards.", to: "/blog#monte-carlo-npv-done-right" },
  { title: "From CSV to signed CPR in one afternoon", tag: "Case study · 15 min", body: "A senior CP takes an iron ore project from raw drillhole database to a signed JORC-aligned CPR PDF in one working session.", to: "/case-studies" },
];

export default function ResourcesPage() {
  useSEO({
    title: "Resources · Docs, blog, case studies and guides",
    description: "Everything you need to master modern resource work — documentation, tutorials, case studies and playbooks curated by working resource geologists.",
    keywords: "SRES resources, mining resource documentation, kriging tutorials",
    path: "/resources",
  });
  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Resources"
        title="Everything you need to master modern resource work."
        description="Documentation, tutorials, playbooks and case studies — curated by working resource geologists and mine engineers."
        testid="resources-hero"
      />

      <Section className="py-20 md:py-24" testid="resources-kits">
        <SectionHeading
          eyebrow="Learning kits"
          title="Six curated starting points."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {KITS.map((k, i) => (
            <Link key={k.title} to={k.to} className="block" data-testid={`resource-kit-${i}`}>
              <FeatureCard icon={k.icon} title={k.title} description={k.description} />
            </Link>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-24 bg-[#F7F8FB]" testid="resources-guides">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <EyebrowLabel className="mb-4">Featured reads</EyebrowLabel>
            <h2 className="font-heading font-black text-slate-900 text-3xl md:text-4xl tracking-tight">Guides worth an afternoon.</h2>
          </div>
          <Link to="/contact" className="text-brand-blue text-sm font-semibold hover:underline" data-testid="resources-request-full">
            Request the full library →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {GUIDES.map((g, i) => (
            <div key={g.title} className="p-7 rounded-2xl bg-white border border-slate-200 flex flex-col" data-testid={`resource-guide-${i}`}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-blue font-mono">{g.tag}</div>
              <div className="mt-3 font-heading font-bold text-slate-900 text-xl leading-snug">{g.title}</div>
              <p className="mt-3 text-slate-600 text-[14px] leading-relaxed flex-1">{g.body}</p>
              <Link to={g.to} className="mt-6 inline-flex items-center gap-2 text-brand-blue text-[13px] font-semibold hover:underline">
                Read now <ArrowRight size={13} weight="bold" />
              </Link>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="resources-cta">
        <CalloutCard
          eyebrow="Prefer a live walkthrough?"
          title="Join a weekly SRES office-hours session on your reporting standard."
          description="Bring your questions on JORC, NI 43-101 or SAMREC — a senior geologist will walk your team through the workflow live."
          cta="Reserve a seat"
          to="/contact"
          testid="resources-cta-callout"
        />
      </Section>
    </div>
  );
}
